// Share Button Component - Add to any content page
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  Share2, Copy, Check, Link, Lock, Calendar, ExternalLink, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ShareButton({ 
  contentType, 
  contentId, 
  workspaceId, 
  title,
  variant = "outline",
  size = "sm",
  className = ""
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Options
  const [customSlug, setCustomSlug] = useState('');
  const [password, setPassword] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(0);
  const [allowDownload, setAllowDownload] = useState(false);

  const createShareLink = async () => {
    setLoading(true);
    
    try {
      const params = new URLSearchParams({
        content_type: contentType,
        content_id: contentId,
        workspace_id: workspaceId,
        title: title || '',
        allow_download: allowDownload
      });
      
      if (customSlug) params.append('custom_slug', customSlug);
      if (usePassword && password) params.append('password', password);
      if (expiresInDays > 0) params.append('expires_in_days', expiresInDays);
      
      const response = await fetch(`${API_URL}/api/share/create?${params}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setShareData(data);
        toast.success('Lien de partage créé !');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création du lien');
      }
    } catch (error) {
      console.error('Failed to create share link:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    const fullUrl = getFullUrl();
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    toast.success('Lien copié !');
    setTimeout(() => setCopied(false), 2000);
  };

  const getFullUrl = () => {
    if (!shareData) return '';
    // Use share_url from API if it contains a full URL, otherwise use current origin
    if (shareData.share_url && shareData.share_url.startsWith('http')) {
      return shareData.share_url;
    }
    return `${window.location.origin}/share/${shareData.token}`;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          <Share2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Share2 className="h-5 w-5 text-purple-500" />
            Partager "{title}"
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {!shareData ? (
            <>
              {/* Options */}
              <div className="space-y-4">
                {/* Custom Slug */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Lien personnalisé (optionnel)</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-sm">/share/</span>
                    <Input
                      value={customSlug}
                      onChange={(e) => setCustomSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="mon-cours-docker"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    Laissez vide pour un lien automatique
                  </p>
                </div>

                {/* Password Protection */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800">
                  <div className="flex items-center gap-3">
                    <Lock className="h-4 w-4 text-slate-400" />
                    <div>
                      <p className="text-sm text-white">Protection par mot de passe</p>
                      <p className="text-xs text-slate-500">Exiger un mot de passe pour accéder</p>
                    </div>
                  </div>
                  <Switch
                    checked={usePassword}
                    onCheckedChange={setUsePassword}
                  />
                </div>
                
                {usePassword && (
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mot de passe"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                )}

                {/* Expiration */}
                <div className="space-y-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Expiration
                  </Label>
                  <select
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(Number(e.target.value))}
                    className="w-full rounded-md bg-slate-800 border-slate-700 text-white p-2"
                  >
                    <option value={0}>Jamais</option>
                    <option value={1}>1 jour</option>
                    <option value={7}>7 jours</option>
                    <option value={30}>30 jours</option>
                    <option value={90}>90 jours</option>
                  </select>
                </div>

                {/* Allow Download */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800">
                  <div>
                    <p className="text-sm text-white">Autoriser le téléchargement</p>
                    <p className="text-xs text-slate-500">Les visiteurs peuvent télécharger le contenu</p>
                  </div>
                  <Switch
                    checked={allowDownload}
                    onCheckedChange={setAllowDownload}
                  />
                </div>
              </div>

              {/* Create Button */}
              <Button 
                onClick={createShareLink} 
                disabled={loading}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Link className="h-4 w-4 mr-2" />
                )}
                Créer le lien de partage
              </Button>
            </>
          ) : (
            /* Share Link Created */
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-green-900/20 border border-green-500/30">
                <p className="text-green-400 text-sm font-medium mb-2">Lien créé avec succès !</p>
                <div className="flex items-center gap-2">
                  <Input
                    value={getFullUrl()}
                    readOnly
                    className="bg-slate-800 border-slate-700 text-white text-sm"
                  />
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    size="icon"
                    className="shrink-0 border-slate-700"
                  >
                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="text-sm text-slate-400 space-y-1">
                {shareData.expires_at && (
                  <p>Expire le: {new Date(shareData.expires_at).toLocaleDateString('fr-FR')}</p>
                )}
                {usePassword && <p>Protégé par mot de passe</p>}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => window.open(getFullUrl(), '_blank')}
                  variant="outline"
                  className="flex-1 border-slate-700 text-slate-300"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Ouvrir
                </Button>
                <Button
                  onClick={() => {
                    setShareData(null);
                    setCustomSlug('');
                    setPassword('');
                    setUsePassword(false);
                    setExpiresInDays(0);
                  }}
                  variant="outline"
                  className="border-slate-700 text-slate-300"
                >
                  Nouveau lien
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
